import { useQuery } from '@tanstack/react-query';
import { ExternalLink, FileWarning } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryKey } from '@/Config/api/queryKey';
import { MissingPageDto } from '@/Domain/api/KnowledgeBase/dto/SyncedSite.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/Domain/components/Shared/Popover/Popover';
import { Spinner } from '@/Domain/components/Shared/Spinner';

type MissingPagesPopoverProps = {
  siteId: number;
  missingCount: number;
};

export const MissingPagesPopover = ({ siteId, missingCount }: MissingPagesPopoverProps) => {
  const { t } = useTranslation('knowledge_base');
  const [open, setOpen] = useState(false);
  const query = useQuery<MissingPageDto[]>({
    queryKey: [QueryKey.knowledgeBase.syncedSites.missingPages, { siteId }],
    enabled: open,
  });

  const items = Array.isArray(query.data) ? query.data : [];

  if (missingCount === 0) {
    return null;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="xs"
          aria-label={t('synced_sites.actions.missing_pages')}
          data-testid={`synced-site-missing-${siteId}`}
        >
          <FileWarning className="size-3.5" />
          {t('synced_sites.actions.missing_pages')}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-0"
        data-testid={`synced-site-missing-popover-${siteId}`}
      >
        <div className="flex flex-col gap-1 border-b border-border p-3">
          <span className="text-sm font-semibold text-foreground">
            {t('synced_sites.missing_pages.title')}
          </span>
          <span className="text-xs text-muted-foreground">
            {t('synced_sites.missing_pages.description')}
          </span>
        </div>

        {query.isLoading ? (
          <div className="flex items-center justify-center p-6">
            <Spinner className="size-4" />
          </div>
        ) : items.length === 0 ? (
          <p className="p-4 text-xs text-muted-foreground">
            {t('synced_sites.missing_pages.empty')}
          </p>
        ) : (
          <ul className="max-h-64 divide-y divide-border overflow-y-auto">
            {items.map((item) => (
              <li key={item.id}>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-accent"
                  title={item.url}
                >
                  <span className="min-w-0 flex-1 truncate text-xs text-foreground">
                    {item.title || item.url}
                  </span>
                  <ExternalLink className="size-3 shrink-0 text-muted-foreground" />
                </a>
              </li>
            ))}
          </ul>
        )}

        <div className="border-t border-border p-3">
          <span className="text-xs text-foreground-subtle">
            {t('synced_sites.missing_pages.kept_note')}
          </span>
        </div>
      </PopoverContent>
    </Popover>
  );
};
